import { useTranslation } from "react-i18next";
import { Search } from "@/components/ui/combobox-search";
import { InputWithAlert } from "@/components/ui/input-with-alert";
import { SelectWithAlert } from "@/components/ui/select-with-alert";
import { DatePicker } from "@/components/ui/date-picker";
import { getClients } from "@/service/weexpertService";
import { INVOICE_TYPE, STATUS, transformFromIdValueToValueLabel, ROLES } from "@/constants";
import { getClientLabel } from "../useInvoiceForm";
import { formatDateString } from "@/styles";

export const MainInfoSection = ({ form, inputChecked = false }) => {
  const { t } = useTranslation();
  const empty_error = t('empty_data_invalid');

  const {
    isCreateMode,
    isEditMode,
    isViewMode,
    isReadOnly,
    user,
    isClient,
    isAdmin,
    client,
    setClient,
    invoiceType,
    setInvoiceType,
    issueDate,
    handleIssueDateChange,
    domainDate,
    handleDomainDateChange,
    status,
    setStatus,
    newInvoiceId,
    setNewInvoiceId,
    dateOfCreationRestriction,
    invoice,
  } = form;

  // View mode
  if (isViewMode) {
    return (
      <div className='w-full'>
        <div className="w-full text-xl font-medium pb-6">
          {t("common:main_information_title")}
        </div>
        <div className='w-full md:w-9/12'>
          <div className='w-full flex justify-between flex-wrap'>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              <InputWithAlert
                label={t("common:client")}
                value={invoice ? `${invoice.client.firstName} ${invoice.client.lastName}` : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("common:type")}
                value={invoice ? INVOICE_TYPE(t).filter(e => e.id === invoice.invoiceType)[0]?.value : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("invoices:invoice_id")}
                value={invoice?.invoiceId ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              <InputWithAlert
                label={t("invoices:date_of_creation")}
                value={invoice ? formatDateString(invoice.createdAt) : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("invoices:date_of_sale")}
                value={invoice ? formatDateString(invoice.domainDate) : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("common:status")}
                value={invoice ? (invoice.state === 'NOT_APPROVED' ? t("invoices:status_in_progress") : t("invoices:status_complete")) : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create/Edit mode
  return (
    <div className='w-full'>
      <div className="w-full text-xl font-medium pb-6">
        {t("common:main_information_title")}
      </div>
      <div className='w-full flex justify-between flex-wrap'>
        <div className={isCreateMode && !isClient ? 'w-full md:w-[49%] m-auto md:m-0' : (isEditMode ? 'w-[96%] md:w-[49%] m-auto md:m-0' : 'hidden')}>
          {isEditMode && (
            <InputWithAlert
              label={t('invoices:document')}
              value={newInvoiceId}
              onChange={(e) => setNewInvoiceId(e.target.value)}
              disabled={!(isEditMode && isAdmin)}
              inputClassName={'h-12 mb-4 bg-white'}
            />
          )}
          {isCreateMode && !isClient && (
            <div className="pt-[2px] mb-0">
              <Search
                fetchOption={(q) => {
                  return getClients(100, 0, q)
                    .then((res) => {
                      return res.data.clients.map(client => ({
                        label: getClientLabel(client),
                        value: client.id,
                        client: client
                      }));
                    });
                }}
                placeholder={t("common:client") + "*"}
                label={t("common:client") + "*"}
                value={client ? getClientLabel(client) : null}
                onChange={(e) => setClient(e.client)}
                error={inputChecked && !client}
                errorMessage={empty_error}
                className={"pb-2"}
              />
            </div>
          )}
          {isEditMode && (
            <InputWithAlert
              readOnly={true}
              label={t("common:client") + "*"}
              value={client ? getClientLabel(client) : ''}
              disabled
              inputClassName={'h-12 mb-4 bg-white'}
            />
          )}
          {!isClient && (
            <SelectWithAlert
              label={t("common:type") + "*"}
              options={INVOICE_TYPE(t).map(transformFromIdValueToValueLabel)}
              value={invoiceType ?? ''}
              onChangeValue={setInvoiceType}
              disabled={!isCreateMode}
              inputClassName={'h-12 mt-2 mb-4 bg-white'}
            />
          )}
        </div>
        <div className='w-full md:w-[49%] m-auto md:m-0'>
          <DatePicker
            label={t("invoices:date_of_creation")}
            date={issueDate}
            defaultMonth={issueDate}
            setDate={handleIssueDateChange}
            error={inputChecked && !issueDate}
            disabled={dateOfCreationRestriction}
            errorMessage={empty_error}
            inputClassName={'w-full min-w-0 max-w-full flex-shrink-0 h-12 text-base mb-4'}
          />
          <DatePicker
            label={t("invoices:date_of_sale")}
            setDate={handleDomainDateChange}
            date={domainDate}
            disabled={{ after: issueDate }}
            defaultMonth={domainDate}
            inputClassName={'w-full h-12 text-base mb-4'}
          />
          {!isClient && (
            <SelectWithAlert
              label={t('status')}
              options={STATUS(t).map(transformFromIdValueToValueLabel)}
              value={status ?? ''}
              onChangeValue={setStatus}
              error={inputChecked && !status}
              errorMessage={empty_error}
              inputClassName={'h-12 mt-2 mb-4 bg-white'}
            />
          )}
        </div>
      </div>
    </div>
  );
};
